import React from 'react';
import { Col, Row, Form as AForm } from 'antd';
import { Field as FField, Form as FForm } from 'react-final-form';
import MakeAsyncFunction from 'react-redux-promise-listener';

import SiderNav from '../../template/SiderNav';
import { MainLayout, SecondaryLayout, StyledContent } from '../../../styles/StyledComponents';
import OrganizationForm from './OrganizationForm';
import { StyledTitle } from '../../fields/Title';
import { InputControl } from '../../fields/Fields';
import { StyledButton } from '../../fields/Button';
import LoadingSpinner from '../../../lib/components/loadingSpinner';
import * as validations from '../../../utils/validations';
import { capitalize } from '../../fields/renderFields';

import usePlans from './hooks/usePlans';

// Imports for the async listener
import { promiseListener, dispatch as storeDispatch } from '../../../store';
import { handleShowToast } from '../../../modules/notification/redux';
import {
  CREATE_ORG_REQUEST,
  CREATE_ORG_SUCCESS,
  CREATE_ORG_FAILURE
} from '../../../modules/account/redux';
import { FormattedMessage } from 'react-intl';

const SetupOrganization = () => {
  const { isLoading, user } = usePlans();

  // 1. If user already has an orgId, show the main OrganizationForm
  if (user?.orgId) {
    return (
      <MainLayout hasSider>
        <SiderNav />
        <SecondaryLayout>
          <StyledContent>
            <Row justify='center'>
              <Col><FormattedMessage id="organization-setup.main.subTitle"/></Col>
            </Row>
            <br />
            <Row justify='center' style={{ textAlign: 'center' }}>
              <StyledTitle><FormattedMessage id="organization-setup.main.title"/></StyledTitle>
            </Row>
            <br />
            <Row justify='center'>
              <Col>
                <OrganizationForm user={user} isLoading={isLoading} />
              </Col>
            </Row>
          </StyledContent>
        </SecondaryLayout>
      </MainLayout>
    );
  }

  // 2. Otherwise, show the School Creation Form with Async Error Handling
  return (
    <MainLayout hasSider>
      <SiderNav />
      <SecondaryLayout>
        <StyledContent>
          <MakeAsyncFunction
            listener={promiseListener}
            start={CREATE_ORG_REQUEST}
            resolve={CREATE_ORG_SUCCESS}
            reject={CREATE_ORG_FAILURE}
            getError={(action) => {
              const payload = action.payload;
              // Shows the toast error just like OrganizationForm
              storeDispatch(handleShowToast({
                message: typeof payload === 'string' ? payload : 'Failed to create school',
                type: 'error'
              }));
            }}
          >
            {(onSubmit) => (
              <FForm
                name='setup-organization-form'
                onSubmit={onSubmit} // The listener handles passing { name } to your Saga
                render={({ handleSubmit, submitting, pristine, hasValidationErrors, dirtySinceLastSubmit, hasSubmitErrors }) => (
                  <LoadingSpinner spinning={isLoading} fullwidth>
                    <Row justify='center'>
                      <Col lg={12} sm={20} xs={24} style={{ textAlign: 'center' }}>
                        <StyledTitle>First, let's create your Organization</StyledTitle>
                        <p>Tell us the name of your institution to get started.</p>
                      </Col>
                    </Row>
                    <br />
                    <Row justify='center'>
                      <Col lg={10} sm={18} xs={24}>
                        <AForm onFinish={handleSubmit} layout='vertical'>
                          <strong>Org Name <span style={{ color: 'red' }}>*</span></strong>
                          <FField
                            name='name'
                            component={InputControl}
                            type='text'
                            placeholder='Organization Name*'
                            format={capitalize}
                            validate={validations.composeValidators(
                              validations.required,
                              validations.minLength(2)
                            )}
                          />
                          <br />
                          <StyledButton
                            size='large'
                            type='primary'
                            htmlType='submit'
                            // Button state logic matches OrganizationForm
                            disabled={submitting || ((hasSubmitErrors || hasValidationErrors) && !dirtySinceLastSubmit) || pristine}
                            loading={isLoading || submitting}
                            block
                          >
                            Create Org & Continue
                          </StyledButton>
                        </AForm>
                      </Col>
                    </Row>
                  </LoadingSpinner>
                )}
              />
            )}
          </MakeAsyncFunction>
        </StyledContent>
      </SecondaryLayout>
    </MainLayout>
  );
};

export default SetupOrganization;
